import React, { useMemo } from "react";
import { Theme, useTheme, css } from "@emotion/react";

import RoundButton from "core/Components/Buttons/RoundButton";
import useBreakpoint from "core/hooks/useBreakpoint";
import { isDown, isUp } from "theme/breakpoints";

type Props = {
    className?: string,
    buttonData: LinkButton,
    buttonHeight?: number,
    buttonSidePadding?: number,
    buttonBorderRadius?: number,
    isUsingAltDesign?: boolean,
};

const CommonButton: React.FC<Props> = ({
    className = "",
    buttonData,
    buttonHeight = undefined,
    buttonSidePadding = undefined,
    buttonBorderRadius = undefined,
    isUsingAltDesign = false,
}) => {
    const theme: Theme = useTheme();
    const { breakpoint } = useBreakpoint();

    const height = useMemo(() => {
        if (!isUsingAltDesign) return undefined;

        switch (true) {
            case isUp(breakpoint, '5xl'):
                return 70;
            case isDown(breakpoint, 'sm'):
                return 51;
            default:
                return 70;
        }
    }, [breakpoint]);

    const linkURL = buttonData.innerURL || buttonData.outerURL;

    if (!buttonData.buttonText || !linkURL) return null;

    const colourTheme = JSON.parse(buttonData.buttonTheme ?? "{}");
    const { buttonTextColour, buttonColour, borderColour } = colourTheme;

    const buttonTextCustomStyle = css`
        font-size: 20px;

        ${isUsingAltDesign && css`
            font-family: ${theme.fonts.figtree};
            padding: 22px 45px;
            font-weight: ${theme.fonts.weights.medium};
        `};

        ${theme.breakpoints.up("5xl")} {
            font-size: 25px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 16px;

            ${isUsingAltDesign && css`
                padding: 13px 22px;
            `};
        }
    `;

    return (
        <RoundButton
            className={className}
            backgroundColour={buttonData.buttonColour || buttonColour || theme.colours.transparent}
            borderColour={borderColour}
            textColour={buttonData.buttonTextColour || buttonTextColour}
            textHoverColour={buttonData.buttonTextHoverColour}
            buttonHoverColour={buttonData.buttonHoverColour}
            linkURL={linkURL}
            buttonHeight={height ?? buttonHeight}
            buttonSidePadding={buttonSidePadding}
            buttonBorderRadius={isUsingAltDesign ? 99 : buttonBorderRadius}
            buttonTextCustomStyle={buttonTextCustomStyle}
        >
            {buttonData.buttonText}
        </RoundButton>
    );
}

export default CommonButton;
